// src/pages/HistoryPage.jsx
import {
  ArrowLeft,
  CheckCircle,
  Star,
  MessageCircle,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { API_ENDPOINTS } from '../utils/constants';
import { fetchWithAuth } from './authService';
import CommentsModal from './CommentModal';
import { Navigate, useNavigate } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';
import { PiShareFat } from 'react-icons/pi';

// ------------ Answer Card ------------
function AnswerCard({ answer, showToast }) {
  const [selectedReaction, setSelectedReaction] = useState(
    answer.userReaction || null
  );
  const [openComments, setOpenComments] = useState(false);
  const [reactionStats, setReactionStats] = useState(
    answer.question.stats || {
      likes: 0,
      dislikes: 0,
      comments: 0,
    }
  );

  // Use question stats for both majority calculation and percentage display
  const majorityIdx = answer.question.stats?.choicePercentages
    ? answer.question.stats.choicePercentages.indexOf(
        Math.max(...answer.question.stats.choicePercentages)
      )
    : -1;

  const handleReaction = async type => {
    const prevReaction = selectedReaction;
    const newStats = { ...reactionStats };

    try {
      if (selectedReaction === type) {
        // Remove reaction
        setSelectedReaction(null);
        if (type === 'like') newStats.likes -= 1;
        if (type === 'dislike') newStats.dislikes -= 1;

        await fetchWithAuth(API_ENDPOINTS.CHANCE_REMOVE_REACTION, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ questionId: answer.questionId }),
        });
      } else {
        // Add or change reaction
        if (selectedReaction === 'like') newStats.likes -= 1;
        if (selectedReaction === 'dislike') newStats.dislikes -= 1;
        if (type === 'like') newStats.likes += 1;
        if (type === 'dislike') newStats.dislikes += 1;
        setSelectedReaction(type);

        await fetchWithAuth(API_ENDPOINTS.CHANCE_REACTION, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questionId: answer.questionId,
            reactionType: type,
          }),
        });
      }
      setReactionStats(newStats);
    } catch (e) {
      // Revert on error
      setSelectedReaction(prevReaction);
      setReactionStats(
        answer.reactionStats || {
          likes: 0,
          dislikes: 0,
          comments: 0,
        }
      );
      showToast('Failed to update reaction');
    }
  };

  const formatCount = num => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num;
  };

  return (
    <article
      style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '1rem',
        marginBottom: '0.75rem',
      }}
    >
      {/* Question */}
      <h3
        style={{
          fontWeight: 'bold',
          fontSize: '1.025rem',
          color: '#1f2937',
          marginBottom: '0.75rem',
          textAlign: 'left',
        }}
      >
        {answer.question.question}
      </h3>

      {/* Results */}
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {answer.question.choices.map((choice, idx) => {
          const picked = answer.selectedChoice === idx;
          const isMajority = idx === majorityIdx;
          const bothSame = picked && isMajority;

          const percentage = Math.round(
            answer.question.stats.choicePercentages?.[idx] || 0
          );

          let baseBg = '#f9fafb';
          if (isMajority) baseBg = '#ecfdf5';
          if (picked && !isMajority) baseBg = '#FFF0F0';

          let borderClr = '#d1d5db';
          if (isMajority) borderClr = '#16a34a';
          if (picked && !isMajority) borderClr = '#DB0F11';

          let progressClr = '#9ca3af';
          if (isMajority) progressClr = '#16a34a';
          if (picked && !isMajority) progressClr = '#DB0F11';

          return (
            <div
              key={idx}
              style={{
                borderRadius: '0.5rem',
                border: `1px solid ${borderClr}`,
                padding: '0.75rem',
                background: baseBg,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.9rem',
                  gap: '1rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    flex: 1,
                  }}
                >
                  <span>{choice}</span>

                  {bothSame && (
                    <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
                      <CheckCircle
                        style={{
                          height: '1rem',
                          width: '1rem',
                          color: '#DB0F11',
                        }}
                      />
                      <Star
                        style={{
                          height: '1rem',
                          width: '1rem',
                          color: '#16a34a',
                        }}
                      />
                    </span>
                  )}
                  {picked && !bothSame && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      <CheckCircle
                        style={{
                          height: '1rem',
                          width: '1rem',
                          color: '#DB0F11',
                        }}
                      />
                      {choice.length <= 18 ? (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#DB0F11',
                          }}
                        >
                          Your Selection
                        </span>
                      ) : choice.length <= 25 ? (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#DB0F11',
                          }}
                        >
                          Yours
                        </span>
                      ) : null}
                    </span>
                  )}
                  {isMajority && !bothSame && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: '#16a34a',
                      }}
                    >
                      <Star style={{ height: '1rem', width: '1rem' }} />
                      {choice.length <= 25 && (
                        <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          Majority
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: 500 }}>{percentage}%</span>
              </div>
              <div
                style={{
                  marginTop: '0.5rem',
                  height: '0.5rem',
                  borderRadius: '9999px',
                  background: '#e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${percentage}%`,
                    height: '100%',
                    borderRadius: '9999px',
                    background: progressClr,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Reactions */}

      <footer
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '0.75rem',
        }}
      >
        <button
          onClick={() => handleReaction('like')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: selectedReaction === 'like' ? '#DB0F11' : '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
          }}
        >
          <ThumbsUp
            fill={selectedReaction === 'like' ? '#DB0F11' : 'none'}
            stroke={selectedReaction === 'like' ? '#DB0F11' : '#6b7280'}
            style={{ height: '1.25rem', width: '1.25rem' }}
          />
          {formatCount(reactionStats.likes ?? 0)}
        </button>

        <button
          onClick={() => handleReaction('dislike')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: selectedReaction === 'dislike' ? '#DB0F11' : '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
          }}
        >
          <ThumbsDown
            fill={selectedReaction === 'dislike' ? '#DB0F11' : 'none'}
            stroke={selectedReaction === 'dislike' ? '#DB0F11' : '#6b7280'}
            style={{ height: '1.25rem', width: '1.25rem' }}
          />
          {formatCount(reactionStats.dislikes ?? 0)}
        </button>

        <button
          onClick={() => setOpenComments(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
          }}
        >
          <MessageCircle
            style={{ height: '1.25rem', width: '1.25rem' }}
            stroke="#6b7280"
          />
          {formatCount(reactionStats.comments ?? 0)}
        </button>

        <button
          onClick={() => {
            const shareMessage = `Everyone’s already answering on Tringbox Chance

Which Bollywood DJ anthem deserves a comeback?

Don’t miss out, match the majority now and grab coins

Redeem them for exciting coupons or play songs 🎁

Play before it’s too late 👉 https://user.tringbox.com/launch/chance/question/${answer.question.questionId}`;

            if (navigator.share) {
              navigator
                .share({
                  title: 'Tringbox Chance',
                  text: shareMessage, // ✅ full message with link
                })
                .catch(err => console.log('Share failed:', err));
            } else {
              navigator.clipboard.writeText(shareMessage);
              showToast('Copied message to clipboard!');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: '#6b7280',
            fontWeight: 500,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
            whiteSpace: 'nowrap',
          }}
        >
          <PiShareFat
            fill="#6b7280"
            stroke="#6b7280"
            style={{ height: '1.3rem', width: '1.3rem' }}
          />
        </button>
        <CommentsModal
          open={openComments}
          onClose={() => setOpenComments(false)}
          questionId={answer.question.questionId}
          showToast={showToast}
          onCommentAdded={() => {
            // ✅ Optimistically increment comment count
            reactionStats.comments = (reactionStats.comments ?? 0) + 1;
          }}
        />
      </footer>
    </article>
  );
}

// ------------ History Page ------------
export default function HistoryPage() {
  const [answers, setAnswers] = useState([]);
  const [page, setPage] = useState(1);
  const { ref, inView } = useInView({ rootMargin: '400px' });
  const [isEnd, setIsEnd] = useState(false);
  const [coins, setCoins] = useState(0);
  const [loading, setLoading] = useState(false);
  const hasFetchedInitial = useRef(false);
  const [isToast, setIsToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const navigate = useNavigate();

  const showToast = msg => {
    setToastMsg(msg);
    setIsToast(true);
    setTimeout(() => setIsToast(false), 2500);
  };

  const fetchAnswers = useCallback(
    async pageNum => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await fetchWithAuth(
          `${API_ENDPOINTS.CHANCE_USER_ANSWER}?page=${pageNum}&limit=10`
        );
        const data = await res.json();
        if (!data.answers || data.answers.length === 0) {
          setIsEnd(true);
        } else {
          setAnswers(prev =>
            pageNum === 1 ? data.answers : [...prev, ...data.answers]
          );
        }
      } catch (error) {
        console.error('Failed to fetch answers:', error);
        showToast('Failed to load answers');
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.USERS);
      const data = await res.json();
      setCoins(data?.coins || 0);
    } catch (e) {
      console.error(e);
      showToast('Failed to load profile');
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchAnswers(1);
      fetchProfile();
    }
  }, [fetchAnswers, fetchProfile]);

  useEffect(() => {
    if (inView && !isEnd && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchAnswers(nextPage);
    }
  }, [inView, isEnd, loading, page, fetchAnswers]);

  return (
    <main
      style={{
        margin: '0 auto',
        maxWidth: '40rem',
        background: '#f1f5f9',
        minHeight: '100vh',
      }}
    >
      {/* Toast Notification */}
      {isToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            borderRadius: '20px',
            padding: '12px 20px',
            minWidth: '200px',
            textAlign: 'center',
          }}
        >
          {toastMsg}
        </div>
      )}

      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IoIosArrowBack
            style={{
              fontSize: '29px',
              color: '#CC151E',
              cursor: 'pointer',
            }}
            onClick={() => navigate(-1)}
          />
          <span
            style={{
              fontWeight: '600',
              color: '#DB0F11',
              fontSize: '1.35rem',
            }}
          >
            Chance History
          </span>
        </div>
        <button
          onClick={() => Navigate('/buy')}
          className="text-end py-0"
          style={{
            position: 'relative',
            border: '1.321px solid #FED111',
            borderRadius: '20px ',
            color: 'black',
            textDecoration: 'none',
            outline: 'none',
            background: 'transparent',
            padding: 0,
          }}
        >
          <span
            className="text-end d-flex flex-row align-items-center"
            style={{
              color: 'black',
              borderColor: 'white',
              gap: '5px',
              paddingRight: '10px',
              paddingLeft: '0px',
            }}
          >
            <img
              src="/images/coin.svg"
              className=""
              style={{
                height: '30px',
              }}
            />
            {coins || 0}
          </span>
        </button>
      </header>

      {/* Answer list */}
      <div style={{ padding: '1rem' }}>
        {answers.map(a => (
          <AnswerCard key={a._id} answer={a} showToast={showToast} />
        ))}
        {loading && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            Loading more answers...
          </div>
        )}
        {!isEnd && <div ref={ref} style={{ height: '40px' }} />}
      </div>
    </main>
  );
}
